import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { formatTime, calculateDrift } from "../utils/videoSyncManager";

const loadVimeoAPI = () => {
  return new Promise((resolve) => {
    if (window.Vimeo) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://player.vimeo.com/api/player.js";
    tag.onload = () => resolve();
    document.getElementsByTagName("head")[0].appendChild(tag);
  });
};

function VimeoPlayer({ videoId, isHost, socketManager, partyStatus }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [drift, setDrift] = useState(0);
  const heartbeatIntervalRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);

  useEffect(() => {
    let player = null;
    const initPlayer = async () => {
      await loadVimeoAPI();
      const iframe = document.createElement("iframe");
      iframe.src = `https://player.vimeo.com/video/${videoId}?controls=0`;
      iframe.frameBorder = "0";
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.position = "absolute";
      iframe.style.top = "0";
      iframe.style.left = "0";
      containerRef.current.innerHTML = ""; // Clear any existing content
      containerRef.current.appendChild(iframe);

      player = new window.Vimeo.Player(iframe);
      playerRef.current = player;

      player.on("loaded", async () => {
        setIsReady(true);
        const dur = await player.getDuration();
        setDuration(dur);
      });

      player.on("play", () => {
        setIsPlaying(true);
        if (isHost) {
          player.getCurrentTime().then((time) => {
            console.log("[WP-DEBUG] Vimeo host play → emitPlay", { time });
            socketManager.emitPlay(time);
          });
        }
      });

      player.on("pause", () => {
        setIsPlaying(false);
        if (isHost) {
          player.getCurrentTime().then(time => socketManager.emitPause(time));
        }
      });

      player.on("seeked", () => {
        if (isHost) {
          player.getCurrentTime().then(time => socketManager.emitSeek(time));
        }
      });
    };

    initPlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, isHost, socketManager]);

  useEffect(() => {
    if (!isReady) return;
    timeUpdateIntervalRef.current = setInterval(async () => {
      if (playerRef.current) {
        const time = await playerRef.current.getCurrentTime();
        setCurrentTime(time);
      }
    }, 100);
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [isReady]);

  useEffect(() => {
    if (!isHost || !isReady) return;
    heartbeatIntervalRef.current = setInterval(async () => {
      if (playerRef.current) {
        const time = await playerRef.current.getCurrentTime();
        const paused = await playerRef.current.getPaused();
        socketManager.emitHeartbeat(time, !paused);
      }
    }, 5000);
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isHost, isReady, socketManager]);

  useEffect(() => {
    if (isHost || !socketManager || !isReady) {
      if (!isHost && socketManager && !isReady) {
        console.log(
          "[WP-DEBUG] Vimeo participant sync listeners NOT attached yet (isReady=false)"
        );
      }
      return;
    }

    console.log("[WP-DEBUG] Vimeo participant sync listeners attached", {
      isReady,
    });

    const handleSyncState = async (data) => {
      console.log("[WP-DEBUG] Vimeo recv sync-state", {
        isReady,
        hasPlayer: !!playerRef.current,
        data,
      });
      if (!playerRef.current) return;
      const { currentTime: targetTime, isPlaying: targetPlaying } = data;
      const currentVideoTime = await playerRef.current.getCurrentTime();
      const currentDrift = calculateDrift(currentVideoTime, targetTime);
      setDrift(currentDrift);

      if (targetPlaying) {
        await playerRef.current.play();
      } else {
        await playerRef.current.pause();
      }

      if (currentDrift > 2) {
        setIsSyncing(true);
        await playerRef.current.setCurrentTime(targetTime);
        setTimeout(() => setIsSyncing(false), 1000);
      }
    };

    const handlePlay = async (data) => {
      console.log("[WP-DEBUG] Vimeo recv play", {
        isReady,
        hasPlayer: !!playerRef.current,
        data,
      });
      if (!playerRef.current) return;
      setIsSyncing(true);
      await playerRef.current.setCurrentTime(data.currentTime);
      await playerRef.current.play();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handlePause = async (data) => {
      if (!playerRef.current) return;
      setIsSyncing(true);
      await playerRef.current.setCurrentTime(data.currentTime);
      await playerRef.current.pause();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handleSeek = async (data) => {
      if (!playerRef.current) return;
      setIsSyncing(true);
      await playerRef.current.setCurrentTime(data.currentTime);
      setTimeout(() => setIsSyncing(false), 500);
    };

    socketManager.on("sync-state", handleSyncState);
    socketManager.on("play", handlePlay);
    socketManager.on("pause", handlePause);
    socketManager.on("seek", handleSeek);

    setTimeout(() => socketManager.requestSync(), 500);

    return () => {
      socketManager.off("sync-state", handleSyncState);
      socketManager.off("play", handlePlay);
      socketManager.off("pause", handlePause);
      socketManager.off("seek", handleSeek);
    };
  }, [isHost, socketManager, isReady]);

  const togglePlayPause = async () => {
    if (!playerRef.current) return;
    const paused = await playerRef.current.getPaused();
    if (paused) {
      await playerRef.current.play();
    } else {
      await playerRef.current.pause();
    }
  };

  const handleSeek = async (e) => {
    if (!playerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    await playerRef.current.setCurrentTime(seekTime);
    if (isHost) {
      socketManager.emitSeek(seekTime);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative bg-slate-950 rounded-xl overflow-hidden border border-slate-700">
      <div ref={containerRef} className="relative w-full aspect-video bg-black" />
      
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-300 text-sm">Loading Vimeo player...</p>
          </div>
        </div>
      )}

      {isSyncing && (
        <div className="absolute top-4 left-4 bg-emerald-500/90 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 z-10">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Syncing...
        </div>
      )}

      {isHost && (
        <div className="absolute top-4 right-4 bg-violet-500/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold z-10">
          HOST
        </div>
      )}

      {!isHost && (
        <div className="absolute top-4 right-4 bg-slate-800/90 text-slate-300 px-3 py-1.5 rounded-full text-xs z-10">
          Following host
        </div>
      )}

      {isReady && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 z-10">
          <div
            className="w-full h-1.5 bg-slate-700 rounded-full cursor-pointer mb-3 group"
            onClick={isHost ? handleSeek : undefined}
          >
            <div
              className="h-full bg-blue-500 rounded-full relative group-hover:bg-blue-400 transition"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition" />
            </div>
          </div>

          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <button
                onClick={isHost ? togglePlayPause : undefined}
                disabled={!isHost}
                className={`p-2 rounded-full transition ${
                  isHost ? "hover:bg-white/20 active:scale-95" : "opacity-50 cursor-not-allowed"
                }`}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>
              <span className="text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              {!isHost && drift > 0.5 && (
                <span className="text-xs text-emerald-400">
                  Drift: {drift.toFixed(1)}s
                </span>
              )}
            </div>
          </div>

          {partyStatus === "waiting" && (
            <div className="mt-2 text-center text-sm text-slate-400">
              Waiting for party to start...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VimeoPlayer;
